/**
 * Central config for the CSV column-matching engine (CRM-1174 + CRM-1175).
 *
 * Two coarse switches and every tunable threshold live here so a developer can
 * fine-tune or disable a layer from one place — no hunting for magic numbers.
 *
 * Deliberately NOT per-tier toggles: the four offline tiers are a fall-through
 * cascade (each runs only on what the previous missed), so disabling tier 2 but
 * keeping tier 4 would create a matcher state that never exists in production.
 * The safe switches are at the layer boundary — offline on/off and AI on/off:
 *
 *   offline + ai   → offline-first, AI on escalation (default, shipped behaviour)
 *   offline only   → never call the gateway; useful for offline dev / testing
 *   ai only        → skip the offline matcher, always ask the AI
 *   both off       → PRE-FEATURE behaviour: parse row 0, no auto-mapping at all;
 *                    the user maps every column by hand (the original modal)
 */

export const matchingConfig = {
  /** Offline fuzzy matcher (the 4-tier cascade). Off → skip straight to AI. */
  offlineEnabled: true,
  /** AI matcher. Off → never call the gateway; keep the offline result. */
  aiEnabled: true,

  /** How many leading rows detectHeaderRow scores when skipping preamble. */
  maxHeaderScanRows: 5,

  /**
   * Header-scan short-circuit: stop scanning as soon as a row matches at least
   * this fraction of its own cells (and ≥2 columns). A clean header row (row 0
   * in most files) hits this immediately, so we don't waste passes scanning
   * rows 1–4. A preamble row matches ~nothing, stays below the bar, and the scan
   * continues. Set to >1 to disable the short-circuit and always scan every row.
   */
  headerConfidentRatio: 0.6,

  /** How many leading raw lines are sent to the AI (header + sample values). */
  maxAiScanRows: 20,

  /**
   * AI escalation trigger. When true, the offline result escalates to the AI iff
   * a mandatory IDENTITY field (familyName/name — schema-derived, computed in the
   * hook) is unmatched. This is deliberately NOT a raw match-percentage floor: a
   * clean file often carries many columns with no CRM target (event/vendor
   * cruft), so a "< X% matched" rule would over-escalate correct results. The
   * identity field is the one signal that means the parse itself is likely wrong.
   */
  escalateOnUnmatchedIdentityField: true,

  /**
   * On escalation, keep the offline matches and let the AI fill only the gaps —
   * but only when the AI parses the file into the SAME columns (same delimiter +
   * header row) as offline did, so indices line up. When they differ, the AI
   * result is taken wholesale (offline is discarded). Set false to always take
   * the AI result wholesale on escalation.
   */
  mergeOfflineWithAi: true,

  /**
   * Abbreviation tier: max gap between two matched characters. A real
   * abbreviation drops short internal runs ("email"→"eml" skips 2); it does not
   * leap across half the word ("civility"→"city" skips 4). 2 keeps the real
   * cases and rejects the coincidences.
   */
  maxAbbreviationGap: 2,

  /**
   * Fuzzy tier: max Levenshtein distance, scaled to header length. Absolute ≤2
   * mis-fires on short tokens (org→url scores 2), so ≤3 chars allow a single
   * edit, longer strings allow 2. Two edits is the practical ceiling.
   */
  fuzzyShortLength: 3,
  fuzzyShortMaxDistance: 1,
  fuzzyLongMaxDistance: 2,

  /**
   * Fuzzy tier floor: skip the comparison when the SHORTER of header/form is
   * below this. A single edit against a 2-char initialism ("nam"→"am") spans
   * far too large a space to mean anything — such ultra-short forms (am, ae, id)
   * are legitimate only at the exact/normalised tier, never as fuzzy targets.
   * Same philosophy as minSubsetTokenLength on the token-subset tier.
   */
  fuzzyMinLength: 3,

  /**
   * Token-subset tier: a lone form token this short (chars) is too weak to use
   * as a subset key — a header containing a stray "id"/"no" would over-match.
   */
  minSubsetTokenLength: 2,

  /**
   * Dev-only batch diagnostic button in the Select-file step. When true, a
   * "Run batch diagnostic" control appears that runs a multi-file selection
   * through the real offline→escalate→AI→merge pipeline and prints a per-column
   * table (column → field, tier, score, duplicate flag) to the console — the
   * only way to exercise the live AI path the Node sweep can't reach. Off in
   * production; flip on locally to re-validate the matcher against the example
   * CSVs after any change to the tiers or synonyms.
   */
  batchDiagnosticEnabled: false,
} as const
